import { useRef, useEffect, useMemo } from "react";
import { Portal } from "@ttc3k/trekker";
import { BookerProps } from "../App";
import { lockScrollbar, unlockScrollbar } from "../shared/utils";
import { i18nLanguage } from "i18n";

/** Safe for embedding in srcDoc attribute values (booker id is hex; this stays defensive). */
function escapeAttr(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

type BookerIFrameProps = Pick<BookerProps, "bookerId" | "bookerPosition"> & {
  open: boolean;
  onClose: () => void;
  language: i18nLanguage;
};

function BookerIFrame({
  bookerId,
  open,
  onClose,
  bookerPosition = "right",
  language,
}: BookerIFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleIframeMessage = (event: MessageEvent) => {
      if (event.data.type === "CLOSE_MODAL") {
        onClose();
      }
    };

    window.addEventListener("message", handleIframeMessage);

    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, [onClose]);

  useEffect(() => {
    const iframe = iframeRef.current;
    const iframeWindow = iframe?.contentWindow;
    if (iframe && iframeWindow && open) {
      iframeWindow.postMessage(
        {
          type: "OPEN_MODAL",
        },
        "*",
      );
    }
  }, [open]);

  // Only lock/unlock the scrollbar this instance owns. Unconditionally calling
  // unlockScrollbar() when closed (including on mount) stomps scroll locks held
  // by others — e.g. the event details slideout's dialog sets
  // `body { overflow: hidden }` while open, and host-page smooth-scroll libraries
  // (Lenis et al) break wheel scrolling inside the slideout once that lock is
  // cleared out from under them.
  useEffect(() => {
    if (!open) return;
    lockScrollbar();
    return () => unlockScrollbar();
  }, [open]);

  const isLocal = import.meta.env.VITE_ENVIRONMENT === "local";
  const iframeProps = useMemo(() => {
    const safeId = escapeAttr(bookerId);
    const safePosition = escapeAttr(bookerPosition);
    const safeLang = escapeAttr(language);

    if (isLocal) {
      // Load a real Vite HTML page (not srcDoc + inline module): react-swc Fast Refresh
      // requires a normal document and fails with "can't detect preamble" in srcDoc iframes.
      const q = new URLSearchParams({
        bookerId,
        bookerPosition,
        language,
      });
      // Absolute URL on this module's origin (the Vite dev server). A root-relative
      // path resolves against the host page, so Book Now on an API-served calling
      // card (`/page/:hash/:slug` on SERVER_URL) requests the modal from the API
      // and Express responds "Cannot GET /widgetBookerModal.html".
      const base = import.meta.env.BASE_URL.endsWith("/")
        ? import.meta.env.BASE_URL
        : `${import.meta.env.BASE_URL}/`;
      const src = new URL(
        `${base}widgetBookerModal.html?${q.toString()}`,
        import.meta.url,
      ).href;
      return { src };
    }

    return {
      srcDoc: `<html lang="en">
              <head>
                <meta charset="UTF-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <title>Checkout Modal</title>
              </head>
              <body>
                <div id="root" data-booker-id="${safeId}" data-booker-position="${safePosition}" data-language="${safeLang}"></div>
                <script
                  type="module"
                  src="${
                    import.meta.env.VITE_API_SERVER_URL
                  }/embeddable/widget/bookerModal/main.js"
                ></script>
              </body>
              </html>`,
    };
  }, [isLocal, bookerId, bookerPosition, language]);

  return (
    <Portal>
      <iframe
        ref={iframeRef}
        style={{
          border: 0,
          position: "fixed",
          width: open ? "100%" : 0,
          height: open ? "100%" : 0,
          top: open ? 0 : -1000,
          left: open ? 0 : -1000,
          opacity: open ? 1 : 0,
          // Must stack above trekker's `zIndex="max"` token (2147483647, the CSS max integer)
          // used by the event details slideout and the multi-checkout launcher modal, otherwise
          // the checkout iframe opens hidden behind them.
          zIndex: 2147483647,
          // Without this, a wheel/touch gesture that the checkout drawer can't consume
          // (e.g. cursor over the transparent backdrop, or content that's reached its
          // scroll boundary) chains across the iframe boundary and scrolls the host page
          // behind it instead, so it looks like the checkout widget can't be scrolled.
          overscrollBehavior: "contain",
          // When the event details slideout (an Ark UI modal dialog) is open, it sets
          // `document.body.style.pointerEvents = "none"` and only re-enables `auto` on its
          // own registered dialog layer(s) to block interaction with everything else while
          // it's the topmost modal. Since this iframe is portaled outside that dialog's
          // layer registry, it inherits `pointer-events: none` from body unless explicitly
          // overridden here — without this, clicks land on whatever's visually behind it.
          pointerEvents: open ? "auto" : "none",
        }}
        {...iframeProps}
      />
    </Portal>
  );
}

export { BookerIFrame };
